'use client';
import './productLibrary.scss';

import { useSpinnerContext } from '@/context/SpinnerContext';
import { triggerEvent } from '@/lib/events';

import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';

import { Box, Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { arrayToCommaDelimatedString, singleProductEncodeSearchParams } from '@/lib/utils';
import ProviderDetailTab from './productLibraryTabDetail';
import { useTranslation } from 'react-i18next';
import { MixpanelTracking } from '@/services/mixpanel';
import { MixpanelEventHub } from '@/services/eventEnums';
import { RestrictLocation } from '../restricted/restrictLocation';

interface GrazingAngle {
  min: number;
  max: number;
}

interface Offering {
  offering_id: string;
  offering_name: string;
  description: string;
  properties: {
    samples: string[];
    geofence?: string[];
    restricted_countries?: string[];
  };
  configurations: Array<{
    id: string;
    name: string;
    parameters: Record<string, unknown>;
    properties: Record<string, unknown>;
    description: string;
  }>;
  revision: number;
  price: number;
  price_type: string;
  start_date: string;
  end_date: string | null;
  category: 'archive' | 'tasking';
  minimum_result_age: number | null;
  maximum_result_age: number | null;
  minimum_billable_aoi_size: number;
  minimum_geometry_aoi_size: number;
  maximum_platform_aoi_size: number;
  on_platform: boolean;
  feasibility_study: boolean;
}

interface ProviderData {
  product_id: string;
  partner_name: string;
  product_name: string;
  category?: string[];
  bands?: string[];
  offerings?: Offering[];
  sensor_type: string;
  sensor_mode: string;
  resolution: number;
  properties: {
    samples: string[];
    sample_preview?: string;
  };
  archiveOfferings?: Offering[];
  taskingOfferings?: Offering[];
  categoryList?: string;
  bandsList?: string;
}

interface ProviderDetailProps {
  id: string;

  isUserLoggedIn: boolean;
  productData: any;
}

export default function ProductLibraryDetail({
  id,

  isUserLoggedIn,
  productData,
}: ProviderDetailProps) {
  const [providerData, setProviderData] = useState<ProviderData | null>(null);
  const [dataProcessing, setDataProcessing] = useState<boolean>(true);

  const { stopLoaderEvent } = useSpinnerContext();
  const { t } = useTranslation('provider');
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [value, setValue] = useState<'1' | '2'>('1');

  const router = useRouter();
  const processProviderData = (data: ProviderData): ProviderData => ({
    ...data,
    archiveOfferings: data?.offerings?.filter(item => item?.category === 'archive') || [],
    taskingOfferings: data?.offerings?.filter(item => item?.category === 'tasking') || [],
    categoryList:
      data?.category && data?.category?.length > 0
        ? arrayToCommaDelimatedString(data?.category, true)
        : '',
    bandsList:
      data?.bands && data?.bands?.length > 0 ? arrayToCommaDelimatedString(data?.bands) : '',
  });

  useEffect(() => {
    if (productData.length > 0) {
      const processedData = processProviderData(productData?.[0]);

      if (!searchParams.toString()) {
        if (processedData?.archiveOfferings && processedData?.archiveOfferings?.length > 0) {
          const urlParams = singleProductEncodeSearchParams('', 'archive');
          window.history.pushState(null, '', pathname + '?' + urlParams);
          if (processedData?.archiveOfferings && processedData?.archiveOfferings?.length === 1) {
            const urlParams = singleProductEncodeSearchParams(
              processedData?.archiveOfferings?.[0]?.offering_id,
              'archive'
            );
            window.history.pushState(null, '', pathname + '?' + urlParams);
          }
        } else if (processedData?.taskingOfferings && processedData?.taskingOfferings?.length > 0) {
          const urlParams = singleProductEncodeSearchParams('', 'tasking');
          window.history.pushState(null, '', pathname + '?' + urlParams);
          if (processedData?.taskingOfferings && processedData?.taskingOfferings?.length === 1) {
            const urlParams = singleProductEncodeSearchParams(
              processedData?.taskingOfferings?.[0]?.offering_id,
              'archive'
            );
            window.history.pushState(null, '', pathname + '?' + urlParams);
          }
        }
      }
      if (searchParams.toString()) {
        const url = searchParams.toString();
        let updatedUrl = '';

        if (url.includes('&nav=false')) {
          updatedUrl = url.replace('&nav=false', '');
        } else if (url.includes('nav=false')) {
          updatedUrl = url.replace('nav=false', '');
        } else {
          updatedUrl = url;
        }

        const updatedParamsUrl = updatedUrl.endsWith('=') ? updatedUrl.slice(0, -1) : updatedUrl;
        const decoded = decodeURIComponent(updatedParamsUrl);
        const params: any = new URLSearchParams(decoded);
        const decodedCategoryParam = params?.get('category');
        const tabValueBasedOnDecodedParam = decodedCategoryParam === 'tasking' ? '2' : '1';
        setValue(tabValueBasedOnDecodedParam);
      }

      setProviderData(processedData);

      if (
        processedData?.archiveOfferings?.length === 0 &&
        processedData?.taskingOfferings &&
        processedData?.taskingOfferings?.length > 0
      ) {
        setValue('2');
      }
    }
    setDataProcessing(false);
    triggerEvent(stopLoaderEvent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopLoaderEvent]);

  return (
    <>
      {providerData?.product_id && (
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <Box className="provider-detail-header">
            <Box className="p-btn-wrapper">
              <Box className="back-btn">
                <Button
                  name="back-button"
                  color="secondary"
                  onClick={() => {
                    router.push('/product-library');
                    MixpanelTracking.getInstance().track(MixpanelEventHub.PROVIDER_PRODUCT_BACK, {
                      product_name: `${providerData?.partner_name} - ${providerData?.product_name}`,
                    });
                  }}
                  startIcon={<KeyboardArrowLeftOutlinedIcon />}
                >
                  <span className="btn-text">{t('back')}</span>
                </Button>
              </Box>
              <Typography variant="h5">
                {`${providerData?.partner_name} - ${providerData?.product_name}`}
              </Typography>
            </Box>
            {providerData?.archiveOfferings &&
            providerData?.archiveOfferings?.length > 0 &&
            providerData?.taskingOfferings &&
            providerData?.taskingOfferings?.length > 0 ? (
              <Box className="provider-tabs">
                {providerData?.archiveOfferings?.length > 0 && (
                  <Button
                    name="archive-button"
                    className={value === '1' ? 'active' : ''}
                    aria-label={value === '1' ? 'archive-button-active' : 'tasking-button-active'}
                    onClick={() => {
                      setValue('1');
                      const offeringForUrlParams =
                        providerData?.archiveOfferings &&
                        providerData?.archiveOfferings?.length === 1
                          ? providerData?.archiveOfferings?.[0]?.offering_id
                          : '';
                      const urlParams = singleProductEncodeSearchParams(
                        offeringForUrlParams,
                        'archive'
                      );
                      window.history.pushState(null, '', pathname + '?' + urlParams);
                      MixpanelTracking.getInstance().track(
                        MixpanelEventHub.PROVIDER_PRODUCT_ARCHIVE_CLICK,
                        {
                          product_name: `${providerData?.partner_name} - ${providerData?.product_name}`,
                        }
                      );
                    }}
                  >
                    {t('archive')}
                  </Button>
                )}
                {providerData?.taskingOfferings?.length > 0 && (
                  <Button
                    name="tasking-button"
                    className={value === '2' ? 'active' : ''}
                    onClick={() => {
                      const offeringForUrlParams = providerData?.taskingOfferings
                        ? providerData?.taskingOfferings?.[0]?.offering_id
                        : '';
                      const urlParams = singleProductEncodeSearchParams(
                        offeringForUrlParams,
                        'tasking'
                      );
                      window.history.pushState(null, '', pathname + '?' + urlParams);
                      setValue('2');
                      MixpanelTracking.getInstance().track(
                        MixpanelEventHub.PROVIDER_PRODUCT_TASKING_CLICK,
                        {
                          product_name: `${providerData?.partner_name} - ${providerData?.product_name}`,
                        }
                      );
                    }}
                  >
                    {t('tasking')}
                  </Button>
                )}
              </Box>
            ) : null}
          </Box>
          <ProviderDetailTab
            isUserLoggedIn={isUserLoggedIn}
            data={providerData}
            type={value === '1' ? 'Archive' : 'Tasking'}
          />
        </Box>
      )}

      {!providerData && !dataProcessing && <RestrictLocation />}
    </>
  );
}
